import { FC, ReactElement, useEffect, useState } from 'react'
import { Table, Form, Row, Col } from 'react-bootstrap'
import { FormCheckType } from 'react-bootstrap/esm/FormCheck'

interface ICheckBoxItem {
  getName(): string
  getValue(): string | number | any
}

export class CheckBoxItem implements ICheckBoxItem {
  private name: string
  private value: string | number | any

  public getName(): string {
    return this.name
  }

  public getValue(): string | number | any {
    return this.value
  }

  public constructor(name: string, value: string | number | any) {
    this.name = name
    this.value = value
  }
}

interface ISelectableCheckBoxItem<TItem extends ICheckBoxItem> {
  getItem(): TItem

  id: string
  isSelected: boolean
}

class SelectableCheckBoxItem<TItem extends ICheckBoxItem>
  implements ISelectableCheckBoxItem<TItem>
{
  id: string
  item: TItem
  isSelected: boolean

  private constructor(item: TItem, id: string, isSelected: boolean = true) {
    this.id = id
    this.item = item
    this.isSelected = isSelected
  }

  public static Create(
    item: ICheckBoxItem,
    id: string
  ): ISelectableCheckBoxItem<ICheckBoxItem> {
    return new SelectableCheckBoxItem(item, id)
  }

  public getItem(): TItem {
    return this.item
  }
}

interface ICheckBoxProps {
  type: FormCheckType
  checkBoxItems: CheckBoxItem[]
  title: string
  showValue: boolean
  selectedData: string
  onSelectionChanged: (checkedItems: ICheckBoxItem[]) => void
}

const CheckBoxList: FC<ICheckBoxProps> = (
  props: ICheckBoxProps
): ReactElement => {
  const [checkBoxItems, setCheckBoxItems] = useState<
    ISelectableCheckBoxItem<ICheckBoxItem>[]
  >([])
  const [allChecked, setAllChecked] = useState<boolean>(true)

  useEffect(() => {
    var setCheckBoxes = props.checkBoxItems.map((value, index) =>
      SelectableCheckBoxItem.Create(value, value.getName() + '_' + index)
    )

    const checkItems = setCheckBoxes.map(checkBoxItem => {
      checkBoxItem.isSelected =
        props.selectedData.search(checkBoxItem.getItem().getValue()) >= 0
      return checkBoxItem
    })

    if (
      checkItems.find(box => {
        return box.isSelected === false
      }) !== undefined
    ) {
      setAllChecked(false)
    }

    setCheckBoxItems(checkItems)
  }, [])

  const onUpdateAll = (selected: boolean): void => {
    const checkItems = checkBoxItems.map(checkBoxItem => {
      checkBoxItem.isSelected = selected
      return checkBoxItem
    })
    setAllChecked(selected)
    setCheckBoxItems(checkItems)
    notifyNewSelection(checkItems)
  }

  const onUpdateSelection = (selected: boolean, itemId: string) => {
    const checkItems = checkBoxItems.map(checkBoxItem => {
      if (itemId && checkBoxItem.id === itemId) {
        checkBoxItem.isSelected = selected
      }

      return checkBoxItem
    })
    setCheckBoxItems(checkItems)
    notifyNewSelection(checkItems)
  }

  const notifyNewSelection = (
    selectableCheckBoxItems: ISelectableCheckBoxItem<ICheckBoxItem>[]
  ) => {
    const checkBoxItems: ICheckBoxItem[] = []
    selectableCheckBoxItems.map(checkBoxItem => {
      if (checkBoxItem.isSelected) {
        return checkBoxItems.push(checkBoxItem.getItem())
      }
      return null
    })

    if (props.onSelectionChanged) {
      props.onSelectionChanged(checkBoxItems)
    }
  }

  const getLabel = (checkBoxItem: ICheckBoxItem): string => {
    let initialString = checkBoxItem.getName()
    if (props.showValue) {
      initialString += ' (' + checkBoxItem.getValue() + ')'
    }
    return initialString
  }

  return (
    <>
      <tr className='border'>
        <td>
          <h5>{props.title}</h5>
        </td>
        <td>
          <Table borderless={true}>
            <tbody>
              <tr>
                <td>
                  <Form>
                    <Form.Group as={Row} className='mb-3'>
                      <Col>
                        <Form.Check
                          id={'Alle' + props.title}
                          name={'Alle' + props.title}
                          label='Alle'
                          type={props.type}
                          checked={allChecked}
                          onChange={e => onUpdateAll(e.target.checked)}
                        />
                      </Col>
                    </Form.Group>
                  </Form>
                </td>
              </tr>
              <tr>
                <td>
                  <Form>
                    <Form.Group as={Row} className='mb-3'>
                      <Col>
                        {checkBoxItems.map((checkBoxItem, index) => (
                          <Form.Check
                            as='input'
                            key={checkBoxItem.getItem().getName() + '_' + index}
                            id={checkBoxItem.getItem().getName() + '_' + index}
                            name={checkBoxItem.getItem().getName()}
                            label={getLabel(checkBoxItem.getItem())}
                            type={props.type}
                            checked={checkBoxItem.isSelected}
                            onChange={e => {
                              onUpdateSelection(
                                e.target.checked,
                                checkBoxItem.getItem().getName() + '_' + index
                              )
                            }}
                          />
                        ))}
                      </Col>
                    </Form.Group>
                  </Form>
                </td>
              </tr>
            </tbody>
          </Table>
        </td>
      </tr>
    </>
  )
}
export default CheckBoxList
